<template>
	<div class="event-menu-container">
		<EventMenuNavbar :event="event" />
		<div class="navbar-page menu-page-container desktop-center">
			<div class="menu-page sk-scrollbar" v-if="!loading">
				<EventDetailsWidget :event="event" />
				<ComingSoonSection v-if="showComingSoon" />
				<slot v-if="showMenu">
					<div
						v-for="category in categorizedProducts"
						:key="category.categoryId"
					>
						<div
							v-if="category.categoryId != -1"
							:ref="`category-${category.categoryId}`"
							class="menu-section-title"
						>
							{{ category.categoryName }}
						</div>
						<div class="menu-items">
							<MenuItem
								v-for="item in category.items"
								:key="item.id"
								:item="item"
								:data-cy="`menu-item-${item.id}`"
							/>
						</div>
					</div>
				</slot>
			</div>
			<FloatingCartSummary
				ref="cartSummary"
				@showGuestCheckoutModal="showGuestCheckoutModal"
			/>
			<div v-if="showMenu">
				<Footer @openCartSummary="openCartSummary" />
			</div>
			<ItemModal ref="itemModal" />
			<GuestCheckoutModal ref="guestCheckoutModal" />
		</div>
	</div>
</template>

<style scoped>
.menu-page-container {
	flex-direction: row;
}

.event-menu-container {
	background: var(--sk-grey-bg);
	height: 100%;
}

.menu-page {
	height: 100%;
	position: relative;
	box-shadow: none;
}

.desktop-center,
.event-menu-container >>> .desktop-center {
	max-width: 1300px !important;
}

@media (max-width: 900px) {
	.menu-page {
		padding-bottom: 70px;
	}
}
</style>

<script>
import { EventStatus } from "@reef-os/utils/enums/eventStatus"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"
import { BusinessType } from "@reef-os/utils/enums/businessType"
import { UniqueProductLabelId } from "@/enums/UniqueProductLabelId"
import menuMixin from "@/mixins/menu-mixin"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import EventMenuNavbar from "@/components/navbars/EventMenuNavbar.vue"
import EventDetailsWidget from "@/components/events/EventDetailsWidget.vue"
import MenuItem from "@/components/menu/MenuItem.vue"
import Footer from "@/components/Footer.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import ComingSoonSection from "@/components/menu/ComingSoonSection.vue"
import ItemModal from "@/components/modals/ItemModal.vue"
import GuestCheckoutModal from "@/components/modals/GuestCheckoutModal.vue"

export default {
	name: "EventMenu",
	mixins: [menuMixin, mobileResponsivenessMixin],
	components: {
		EventMenuNavbar,
		EventDetailsWidget,
		MenuItem,
		Footer,
		FloatingCartSummary,
		ComingSoonSection,
		ItemModal,
		GuestCheckoutModal
	},
	computed: {
		showMenu() {
			return !this.showComingSoon
		},
		eventId() {
			return this.$route.params.eventId
		},
		event() {
			return this.$store.state.businessEvents.find(
				(event) => event.id == this.eventId
			)
		},
		categorizedProducts() {
			const categories = {
				"-1": {
					categoryName: "",
					categoryId: -1,
					items: []
				}
			}
			const menu = this.$store.state.business.menus.find(
				(menu) => menu.event_id == this.eventId
			)
			const categoryOrder =
				menu != undefined
					? this.$store.getters.menusHash[menu.id].categoryOrder
					: []

			this.menu.forEach((item) => {
				if (item.labels) {
					const hasPreorderOnlyLabel = item.labels.some(
						(label) =>
							label.id == UniqueProductLabelId.PREORDER_ONLY
					)
					const isOngoingEvent =
						this.event && this.event.status == EventStatus.ONGOING
					if (hasPreorderOnlyLabel && isOngoingEvent) {
						return
					}
				}

				let categoryId =
					item.category_id != null ? item.category_id : -1
				if (!categories[categoryId]) {
					let name = this.$store.state.categories[categoryId]
						? this.$store.state.categories[categoryId]
						: ""
					categories[categoryId] = {
						categoryName: name,
						categoryId: categoryId,
						items: []
					}
				}
				categories[categoryId].items.push(item)
			})
			return Object.values(categories).sort((a, b) => {
				if (a.categoryId == -1) {
					return -1
				} else {
					return (
						categoryOrder.indexOf(Number(a.categoryId)) -
						categoryOrder.indexOf(Number(b.categoryId))
					)
				}
			})
		}
	},
	methods: {
		openCartSummary() {
			this.$refs.cartSummary.open()
		},
		showGuestCheckoutModal(options) {
			this.$refs.guestCheckoutModal.open(options)
		}
	},
	created: function () {
		this.$store.dispatch("setCurrentTable", -1)
	},
	mounted: function () {
		const canOrderFromEvent =
			this.event &&
			(this.event.status == EventStatus.PREORDER ||
				this.event.status == EventStatus.ONGOING)
		if (!canOrderFromEvent) {
			const businessType = this.$store.state.business
				? this.$store.state.business.type
				: BusinessType.BAR
			const errorNotificationMessage =
				"We're sorry, but that event is already over. Feel free to explore our current offerings!"
			let errorNotificationType = NotificationType.ERROR
			if (businessType == BusinessType.RESIDENTIAL) {
				this.$router.push(`/${this.$route.params.slug}`)
			} else {
				this.$router.push(`/${this.$route.params.slug}/menu`)
			}
			eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
				message: errorNotificationMessage,
				type: errorNotificationType
			})
		}
	}
}
</script>

